import { useEffect, useState } from 'react'
import styled from 'styled-components'
import get from 'lodash/get'
import { useFormikContext } from 'formik'
import { Button, Grid, Wrapper } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'

import Address from 'components/address'
import EstateCard from 'components/estate-card'
import { Input, InputFloating, SelectInput } from 'components/form'
import NotesInput from 'components/notes-input'
import Checkbox from 'components/form/checkbox'
import MessageBox from 'components/message-box'
import { ProbateLeadAttributes } from 'lib/models/lead'
import { email as isValidEmail } from 'lib/formik/validation'
import { ProbateEstatePerson } from 'lib/models/probate-estate'
import { strToBool } from 'utils/helpers'
import { PR_ROLE_OPTIONS } from 'utils/enums'
import { showModal } from 'state/actions'
import store from 'state/create-store'
import DeleteModal from '../delete-modal'
import withForm from '../with-form'
import {
  ESTATE_PEOPLE_ATTRIBUTES,
  PRIMARY_EXECUTOR_TOOLTIP_TEXT,
} from './constants'
import { usePeople } from '../helpers/people-context'
import { Address as AddressType } from 'lib/models/address'
import Tooltip from 'components/tooltip'
import { cannotBePrimaryExecutorOrAdministrator } from './helpers'

const StyledCheckboxesWrapper = styled(Wrapper)`
  display: flex;
  flex-wrap: wrap;

  && {
    margin-top: -${GTR.S};
  }
`

const StyledCheckboxWrapper = styled(Wrapper)`
  margin-right: ${GTR.L};

  && {
    margin-top: ${GTR.S};
  }
`

const Executor = ({
  person,
  listLength,
  listPosition,
  callerIsExecutor,
}: {
  person: ProbateEstatePerson
  listLength: number
  listPosition: number
  callerIsExecutor: boolean
}) => {
  const { deletePerson, updatePerson, people } = usePeople()
  const { values, setFieldValue } = useFormikContext<{
    isPointofContact: string
    isCustomer: boolean
    personalRepresentativeType: string
    personalRepresentativeRole: string
    address: AddressType
  }>()
  const [cannotBePrimaryExecutor, setCannotBePrimaryExecutor] = useState(
    cannotBePrimaryExecutorOrAdministrator(values.personalRepresentativeRole)
  )
  const isCustomer = strToBool(
    get(values, ESTATE_PEOPLE_ATTRIBUTES.IS_CUSTOMER)
  )

  const customerAssignedToQuote = people.find((p) => p.attributes.isCustomer)

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const address = get(values, ESTATE_PEOPLE_ATTRIBUTES.ADDRESS)!

  const onTrashClick = () => {
    store.dispatch(
      showModal({
        component: DeleteModal,
        headingText: 'Delete person',
        config: {
          deleteCallback: () => {
            deletePerson(person.id)
          },
          type: 'person',
          resourceId: person.id,
        },
      })
    )
  }

  const handleSave = ({
    name,
    value,
  }: {
    name: string
    value: string | number | null | object
  }) => {
    updatePerson({
      id: person.id,
      attributes: {
        [name]: value || null,
      },
    })
  }

  const handleUpdatePersonalRepresentativeRole = ({
    name,
    value,
  }: {
    name: string
    value: string | number | null | object
  }) => {
    const cannotBePrimary = cannotBePrimaryExecutorOrAdministrator(
      value as string
    )
    setCannotBePrimaryExecutor(cannotBePrimary)

    if (cannotBePrimary) {
      setFieldValue(ESTATE_PEOPLE_ATTRIBUTES.IS_CUSTOMER, 'false')
    }

    updatePerson({
      id: person.id,
      attributes: {
        [name]: value || null,
        isCustomer: cannotBePrimary ? false : strToBool(values.isCustomer),
      },
    })
  }

  return (
    <EstateCard
      onTrashClick={onTrashClick}
      listLength={listLength}
      listPosition={listPosition}
      type={callerIsExecutor ? 'Additional executor' : 'Executor'}
    >
      <Grid style={{ gridAutoFlow: 'row' }}>
        {!callerIsExecutor ? (
          <Grid.Item>
            <StyledCheckboxesWrapper>
              <StyledCheckboxWrapper>
                <Tooltip content={PRIMARY_EXECUTOR_TOOLTIP_TEXT}>
                  <Checkbox
                    name={ESTATE_PEOPLE_ATTRIBUTES.IS_CUSTOMER}
                    label="Primary executor"
                    checked={isCustomer}
                    disabled={
                      cannotBePrimaryExecutor ||
                      (customerAssignedToQuote && !isCustomer)
                    }
                    handleSave={handleSave}
                  />
                </Tooltip>
              </StyledCheckboxWrapper>
            </StyledCheckboxesWrapper>
          </Grid.Item>
        ) : null}
        <Grid.Item span={2}>
          <InputFloating
            name={ESTATE_PEOPLE_ATTRIBUTES.TITLE}
            label="Title"
            handleSave={handleSave}
          />
        </Grid.Item>
        <Grid.Item span={5}>
          <InputFloating
            name={ESTATE_PEOPLE_ATTRIBUTES.FIRST_NAMES}
            label="First + middle names"
            handleSave={handleSave}
          />
        </Grid.Item>
        <Grid.Item span={5}>
          <InputFloating
            name={ESTATE_PEOPLE_ATTRIBUTES.LAST_NAME}
            label="Last name"
            handleSave={handleSave}
          />
        </Grid.Item>
        <Grid.Item>
          <SelectInput
            name={ESTATE_PEOPLE_ATTRIBUTES.PERSONAL_REPRESENTATIVE_ROLE}
            label="Their role"
            options={PR_ROLE_OPTIONS}
            handleSave={handleUpdatePersonalRepresentativeRole}
          />
        </Grid.Item>
        <Grid.Item span={6} startColumn={1}>
          <Input
            name={ESTATE_PEOPLE_ATTRIBUTES.DATE_OF_BIRTH}
            label="Date of birth"
            type="date"
            handleSave={handleSave}
          />
        </Grid.Item>
        <Grid.Item span={6} startColumn={1}>
          <InputFloating
            name={ESTATE_PEOPLE_ATTRIBUTES.EMAIL}
            label="Email"
            type="email"
            handleSave={handleSave}
            validate={isValidEmail}
          />
        </Grid.Item>
        <Grid.Item span={6}>
          <InputFloating
            name={ESTATE_PEOPLE_ATTRIBUTES.PHONE_NUMBER}
            label="Phone number"
            handleSave={handleSave}
          />
        </Grid.Item>
        <Grid.Item span={9}>
          <Address
            label="Address"
            name={`${ESTATE_PEOPLE_ATTRIBUTES.ADDRESS}.${address.id}}`}
            data={address}
          />
        </Grid.Item>
        <Grid.Item>
          <NotesInput
            name={ESTATE_PEOPLE_ATTRIBUTES.NOTES}
            handleSave={handleSave}
          />
        </Grid.Item>
      </Grid>
    </EstateCard>
  )
}

const ExecutorWithForm = withForm(Executor)

const WillExecutors = () => {
  const { people, createPerson } = usePeople()
  const { values, setFieldValue } = useFormikContext<ProbateLeadAttributes>()

  // 1. How many executors is needed in total?
  const totalExecutorsAmount = values.quoteAttributes.willNumberOfExecutors || 0

  // 2. Is caller the executor?
  const isCallerExecutor = people.find(
    (p) => p.attributes.originalContactId && p.attributes.isCustomer
  )

  // 3. How many executors are already added?
  const executors = people.filter(
    (person) =>
      person.attributes[
        ESTATE_PEOPLE_ATTRIBUTES.PERSONAL_REPRESENTATIVE_TYPE
      ] === 'executor' && person.id !== isCallerExecutor?.id
  )
  // 4. How many executors is still needed?
  const howManyExecutorsIsStillNeeded =
    totalExecutorsAmount - (isCallerExecutor ? 1 : 0) - executors.length

  useEffect(() => {
    if (!values.quoteAttributes.willNumberOfExecutors) return

    if (howManyExecutorsIsStillNeeded < 0) {
      setFieldValue(
        'quoteAttributes.willNumberOfExecutors',
        values.quoteAttributes.willNumberOfExecutors + 1
      )
    }
  }, [
    people,
    values.quoteAttributes.willNumberOfExecutors,
    values.quoteAttributes.customerIsExecutor,
    executors,
    howManyExecutorsIsStillNeeded,
    setFieldValue,
  ])

  return (
    <>
      {executors && executors.length > 0 && (
        <Grid.Item>
          <Grid margin={[0, 0, 'M', 'M']}>
            {executors.map((executor, i) => (
              <Grid.Item key={executor.id} span={11}>
                <ExecutorWithForm
                  initialValues={executor.attributes}
                  enableReinitialize={false}
                  person={executor}
                  listPosition={i + 1}
                  listLength={executors.length}
                  callerIsExecutor={!!isCallerExecutor}
                />
              </Grid.Item>
            ))}
          </Grid>
        </Grid.Item>
      )}
      {howManyExecutorsIsStillNeeded > 0 && (
        <>
          <Grid.Item>
            <MessageBox warning>
              We need details of {howManyExecutorsIsStillNeeded} more{' '}
              {howManyExecutorsIsStillNeeded > 1 ? 'executors' : 'executor'}
            </MessageBox>
          </Grid.Item>
          <Grid.Item>
            <Button.Secondary
              style={{ paddingTop: GTR.XXS, paddingBottom: GTR.XXS }}
              type="button"
              onClick={() => {
                createPerson({
                  personalRepresentativeType: 'executor',
                })
              }}
            >
              Add executor details
            </Button.Secondary>
          </Grid.Item>
        </>
      )}
    </>
  )
}

export default WillExecutors
